import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { equals } from '@softline/core';
import { DateParser } from '@softline/ui-core';
import { SOFTLINE_SERVICE_APPEARANCE } from '@softline/ui-core';
import { DateRange } from '@softline/ui-core';
import { DateRangePickerMobiscrollHostComponent } from './date-range-picker-mobiscroll-host.component';
import {
  DATE_RANGE_PICKER_DEFAULT_SHORTCUTS,
  DateRangePickerSelectionMode,
  DateRangePickerShortcut,
} from './date-range-picker.types';

interface DateRangePickerSelection {
  from: string | null;
  to: string | null;
}

@Component({
  selector: 'soft-date-range-picker, soft-ds-date-range-picker',
  imports: [DateRangePickerMobiscrollHostComponent],
  templateUrl: './date-range-picker.component.html',
  styleUrl: './date-range-picker.component.css',
  host: {
    '[class]': 'hostClasses()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateRangePickerComponent {
  readonly appearanceService = inject(SOFTLINE_SERVICE_APPEARANCE);
  readonly dateParser = inject<DateParser<string>>(DateParser);

  readonly value = input<DateRange | null>(null);
  readonly selectedValue = signal<DateRangePickerSelection | null>(null);
  readonly showShortcuts = input(true);
  readonly showWeekNumbers = input(false);
  readonly showRangeLabels = input(true);
  readonly showOuterDays = input(true);
  readonly shortcuts = input<readonly DateRangePickerShortcut[] | null>(null);
  readonly startField = input<'from' | 'to'>('from');
  readonly selectionMode = input<DateRangePickerSelectionMode>('range');

  readonly endpoint = input<boolean>(false);
  readonly firstSelectDay = input<number>(1);
  readonly selectSize = input<number>();

  readonly valueChange = output<DateRange | null>();
  readonly selectShortcut = output<DateRange>();

  private readonly valueEffect = effect(() => {
    this.selectedValue.set(this.value());
  });

  protected readonly hostClasses = computed(
    () => 'soft-date-range-picker-host soft-ds-date-range-picker-host'
  );

  readonly themeVariant = computed(() => {
    const isDarkMode = this.appearanceService.isDarkMode?.();
    if (isDarkMode !== undefined) return isDarkMode ? 'dark' : 'light';
    if (this.appearanceService.systemDarkModeEnabled()) return 'dark';
    return 'light';
  });

  protected readonly defaultShortcuts = DATE_RANGE_PICKER_DEFAULT_SHORTCUTS;

  readonly activeShortcuts = computed(
    () => this.shortcuts() ?? this.defaultShortcuts
  );

  readonly resolvedShortcuts = computed(() => {
    if (!this.showShortcuts()) return [];

    return this.activeShortcuts()
      .map((shortcut) => {
        const from = shortcut.value.from ? this.dateParser.parse(shortcut.value.from) : null;
        const to = shortcut.value.to ? this.dateParser.parse(shortcut.value.to) : null;

        if (!from || !to) return null;

        return {
          ...shortcut,
          resolvedValue: { from, to },
        };
      })
      .filter((shortcut): shortcut is DateRangePickerShortcut & { resolvedValue: DateRange } =>
        Boolean(shortcut)
      );
  });

  readonly valueArray = computed(() => {
    const value = this.selectedValue();
    return value ? [value.from, value.to] : [null, null];
  });

  // string locator + structural type: a class reference outside `imports` would defeat @defer
  private readonly mobiscrollHost =
    viewChild<{ activateEndDate(): void }>('mobiscrollHost');

  onValueChange(valueArray: [string | null, string | null]): void {
    const [from, to] = valueArray;
    const currentValue = this.selectedValue();
    const endpointEditValue = this.resolveEndpointEditValue(from, to, currentValue);

    if (endpointEditValue) {
      this.selectedValue.set(endpointEditValue);
      this.valueChange.emit(endpointEditValue);
      return;
    }

    const value = from || to ? { from, to } : null;

    if (equals(currentValue, value)) return;
    this.selectedValue.set(value);

    if (!from && !to) {
      this.valueChange.emit(null);
      return;
    }

    if (!from || !to) return;

    this.mobiscrollHost()?.activateEndDate();

    this.valueChange.emit({ from, to });
  }

  protected onCalendarPageLoaded(): void {
    this.activateConfiguredStartField();
  }

  onSelectShortcut(shortcut: DateRangePickerShortcut): void {
    const from = shortcut.value.from ? this.dateParser.parse(shortcut.value.from) : null;
    const to = shortcut.value.to ? this.dateParser.parse(shortcut.value.to) : null;

    if (!from || !to) return;

    const value: DateRange = { from, to };
    this.setValue(value, true);
    this.selectShortcut.emit(value);
  }

  formatShortDate(isoDate: string | null): string {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('de-AT', { day: 'numeric', month: 'short' });
  }

  private setValue(value: DateRange, emitChange: boolean): void {
    this.selectedValue.set(value);
    if (emitChange) {
      this.valueChange.emit(value);
    }
  }

  private activateConfiguredStartField(): void {
    const value = this.selectedValue();
    if (value?.from && this.startField() === 'to') {
      this.mobiscrollHost()?.activateEndDate();
    }
  }

  private resolveEndpointEditValue(
    from: string | null,
    to: string | null,
    currentValue: DateRangePickerSelection | null
  ): DateRange | null {
    if (
      !this.endpoint() ||
      !currentValue?.from ||
      !currentValue?.to
    ) {
      return null;
    }

    if (this.startField() === 'from' && from && !to && from <= currentValue.to) {
      return { from, to: currentValue.to };
    }

    if (this.startField() === 'to' && !from && to && to >= currentValue.from) {
      return { from: currentValue.from, to };
    }

    return null;
  }
}
